import type { components } from "@oathera/contract/api";
import type { Measure, StatusLevel } from "../../components/index.ts";
import type {
  AssuranceSummary,
  AssuranceFinding,
  AssuranceFindingPage,
} from "./use-assurance.ts";

/**
 * Presentation vocabulary for the Code Assurance screen (task 7.4; design §10, requirement 13.4). It
 * keeps the one-way maps from the generated contract shapes onto the screen's presentational pieces
 * in a single, testable place and defines NO API shapes (those come from `@oathera/contract/api` via
 * `use-assurance.ts`).
 *
 * It carries, in one place:
 * - how a `Quantity`'s exactness becomes an exact/estimate/unverifiable `Measure` (design 12.3) and
 *   its numeric value treated as zero when the server reported no data;
 * - human labels + a stable catalog ORDER for every assurance enum (verdict, surface, severity,
 *   engine, status, action, actor type), so the tiles and the filterable table read consistently
 *   regardless of the order the server returned rows in;
 * - the SEVERITY as a non-colour conveyance (design 12.4): a stable rank so critical/high sort to
 *   the top, a {@link StatusLevel} for the icon+text `StatusBadge`, and a prominence flag for a
 *   redundant non-colour emphasis — colour is never the only channel, and a finding is never shown
 *   as a reassuring green;
 * - the derivation that ranks agents by findings introduced (grouped from the page's `items`);
 * - the GATEWAY_ASSURANCE emptiness check — true when the summary AND the findings are entirely
 *   zero/empty, which is what an operator sees when the flag is off (default) or no scans ran;
 * - the drill-down `/events` query strings (the assurance endpoints attach no server-built `drill`,
 *   so the screen composes honest standing filters from real `/events` params — never an invented
 *   route — consistent with the enforcement/posture screens).
 */

type Quantity = components["schemas"]["Quantity"];
type Count = components["schemas"]["count"];
type Verdict = components["schemas"]["enum_assurance_verdict"];
type Surface = components["schemas"]["enum_assurance_surface"];
type Severity = components["schemas"]["enum_finding_severity"];
type Engine = components["schemas"]["enum_assurance_engine"];
type Status = components["schemas"]["enum_finding_status"];
type Action = components["schemas"]["enum_assurance_action"];
type ActorType = components["schemas"]["enum_actor_type"];

/* ---- Quantity → measure/value (design 12.3) ------------------------------------------------- */

/** Map a `Quantity`'s exactness onto the shared exact/estimate vocabulary (design 12.3). */
export function quantityMeasure(q: Quantity): Measure {
  // No data cannot be verified: neither an exact counter nor a sampled estimate, so never green.
  if (q.value === null) return "unverifiable";
  return q.exactness === "estimated" ? "estimate" : "exact";
}

/** A `Quantity`'s numeric value treated as zero when the server reported no data (null). */
export function quantityValue(q: Quantity): number {
  return q.value ?? 0;
}

/* ---- Catalog orders (the frozen presentation order) ----------------------------------------- */

/** Scan verdicts in a stable presentation order (`other` last). Mirrors `enum_assurance_verdict`. */
export const VERDICT_ORDER: readonly Verdict[] = ["pass", "advisory", "block", "error", "other"];

/** Scan surfaces in a stable presentation order (`other` last). Mirrors `enum_assurance_surface`. */
export const SURFACE_ORDER: readonly Surface[] = [
  "agent_tool",
  "inline_guard",
  "ci",
  "repo",
  "other",
];

/**
 * Finding severities from HIGHEST to lowest, `other` last. Drives the tile order, the by-severity
 * breakdown and the severity filter so the most serious findings surface first (design 12.2).
 * Mirrors `enum_finding_severity`.
 */
export const SEVERITY_ORDER: readonly Severity[] = [
  "critical",
  "high",
  "medium",
  "low",
  "info",
  "other",
];

/** Finding engines in a stable presentation order (`other` last). Mirrors `enum_assurance_engine`. */
export const ENGINE_ORDER: readonly Engine[] = [
  "sast",
  "secrets",
  "sca",
  "iac",
  "sbom",
  "mcp_surface",
  "other",
];

/** Finding statuses in a stable presentation order (`other` last). Mirrors `enum_finding_status`. */
export const STATUS_ORDER: readonly Status[] = [
  "open",
  "fixed",
  "accepted",
  "false_positive",
  "other",
];

/* ---- Severity: the non-colour conveyance (design 12.4) -------------------------------------- */

/**
 * Severity rank: higher is more severe. Drives sorting so critical/high surface to the top (design
 * 12.2). `other` ranks LOWEST (−1) deliberately — an uncategorised severity is not more severe than
 * a known one and should not jump ahead of real findings.
 */
export function severityRank(severity: Severity): number {
  switch (severity) {
    case "critical":
      return 5;
    case "high":
      return 4;
    case "medium":
      return 3;
    case "low":
      return 2;
    case "info":
      return 1;
    case "other":
      return -1;
  }
}

/**
 * Map a severity onto a status level for the icon+text `StatusBadge` (design 12.4). critical/high are
 * `error`; medium is `warn`; low/info are `neutral` (real-but-lower severities, not a "good" green);
 * the catalog `other` is grey `unknown` — never green (design 12.3). No severity maps to `healthy`:
 * an assurance FINDING is never a reassuring green.
 */
export function severityStatus(severity: Severity): StatusLevel {
  switch (severity) {
    case "critical":
    case "high":
      return "error";
    case "medium":
      return "warn";
    case "low":
    case "info":
      return "neutral";
    case "other":
      return "unknown";
  }
}

/** Human label for a finding severity. */
export function severityLabel(severity: Severity): string {
  switch (severity) {
    case "critical":
      return "Critical";
    case "high":
      return "High";
    case "medium":
      return "Medium";
    case "low":
      return "Low";
    case "info":
      return "Info";
    case "other":
      return "Other";
  }
}

/** A longer accessible phrase for a severity, used as a badge's `title`. */
export function severityTitle(severity: Severity): string {
  return `Severity: ${severityLabel(severity).toLowerCase()}.`;
}

/**
 * Whether a severity should render PROMINENTLY (critical or high). Components use this to add a
 * redundant, non-colour emphasis (a left rule / heavier weight) so the most serious findings stand
 * out without relying on hue (design 12.2 "surfaced prominently"; 12.4 "not colour alone").
 */
export function severityProminent(severity: Severity): boolean {
  return severity === "critical" || severity === "high";
}

/* ---- Verdict / surface / engine / status / action labels ------------------------------------ */

/** Human label for a scan verdict. */
export function verdictLabel(verdict: Verdict): string {
  switch (verdict) {
    case "pass":
      return "Pass";
    case "advisory":
      return "Advisory";
    case "block":
      return "Block";
    case "error":
      return "Error";
    case "other":
      return "Other";
  }
}

/** Human label for the surface a scan ran on. */
export function surfaceLabel(surface: Surface): string {
  switch (surface) {
    case "agent_tool":
      return "Agent tool";
    case "inline_guard":
      return "Inline guard";
    case "ci":
      return "CI";
    case "repo":
      return "Repository";
    case "other":
      return "Other";
  }
}

/** Human label for the engine that produced a finding. */
export function engineLabel(engine: Engine): string {
  switch (engine) {
    case "sast":
      return "SAST";
    case "secrets":
      return "Secrets";
    case "sca":
      return "SCA";
    case "iac":
      return "IaC";
    case "sbom":
      return "SBOM";
    case "mcp_surface":
      return "MCP surface";
    case "other":
      return "Other";
  }
}

/**
 * Human label for an engine, null-safe. The finding's `engine` is nullable per contract; a `null` is
 * shown honestly as "Unknown engine" rather than a fabricated engine.
 */
export function engineLabelOrNull(engine: Engine | null | undefined): string {
  return engine === null || engine === undefined ? "Unknown engine" : engineLabel(engine);
}

/** Human label for a finding lifecycle status. */
export function statusLabel(status: Status): string {
  switch (status) {
    case "open":
      return "Open";
    case "fixed":
      return "Fixed";
    case "accepted":
      return "Accepted";
    case "false_positive":
      return "False positive";
    case "other":
      return "Other";
  }
}

/**
 * Map a finding status onto a status level for the icon+text `StatusBadge` (design 12.4). `open` is
 * `warn` (an unresolved finding, but not an error on its own); `fixed` is `healthy` (the one place a
 * green is honest — the finding is resolved); `accepted` and `false_positive` are `neutral`
 * (deliberate, non-alarming dispositions); the catalog `other` is grey `unknown`.
 */
export function statusStatus(status: Status): StatusLevel {
  switch (status) {
    case "open":
      return "warn";
    case "fixed":
      return "healthy";
    case "accepted":
    case "false_positive":
      return "neutral";
    case "other":
      return "unknown";
  }
}

/** Human label for a decision taken on a finding (the decision history). */
export function actionLabel(action: Action): string {
  switch (action) {
    case "allow":
      return "Allowed";
    case "advisory":
      return "Advisory";
    case "block":
      return "Blocked";
    case "exception_requested":
      return "Exception requested";
    case "exception_granted":
      return "Exception granted";
    case "fix_verified":
      return "Fix verified";
    case "other":
      return "Other";
  }
}

/**
 * Map a decision action onto a status level for icon+text display (design 12.4). `block` is `error`;
 * `exception_requested` is `warn` (a pending ask); `exception_granted` is `neutral` (a deliberate
 * allowance, not a reassuring green); `fix_verified` is `healthy` (an honest green — the issue was
 * resolved and verified); `allow`/`advisory` are `neutral`; the catalog `other` is grey `unknown`.
 */
export function actionStatus(action: Action): StatusLevel {
  switch (action) {
    case "block":
      return "error";
    case "exception_requested":
      return "warn";
    case "fix_verified":
      return "healthy";
    case "exception_granted":
    case "allow":
    case "advisory":
      return "neutral";
    case "other":
      return "unknown";
  }
}

/** Human label for the actor type that took a decision, null-safe. */
export function actorTypeLabel(actor: ActorType | null | undefined): string {
  if (actor === null || actor === undefined) return "Unknown actor";
  switch (actor) {
    case "owner":
      return "Owner";
    case "admin":
      return "Admin";
    case "system":
      return "System";
    case "agent":
      return "Agent";
    case "other":
      return "Other";
  }
}

/* ---- Agents ranked by findings introduced --------------------------------------------------- */

/** One row of the agents-ranked-by-findings breakdown. */
export interface AgentFindingRank {
  /** The pseudonymous agent reference. */
  readonly agent: string;
  /** How many of the page's findings this agent introduced. */
  readonly count: number;
  /** The most severe severity among this agent's findings (for emphasis/sort). */
  readonly worstSeverity: Severity;
}

/**
 * Rank agents by the number of findings they introduced in the page's `items`, most findings first,
 * tie-broken by worst severity then by agent id (stable). Findings with no `agent_sub` are grouped
 * out — an unattributed finding has no agent to rank, so it is honestly excluded rather than bucketed
 * under a fabricated "unknown agent" row. The ranking is over the CURRENT page of findings (the same
 * `items` the table shows), so it reflects exactly the window and filters in effect.
 */
export function rankAgentsByFindings(
  findings: readonly AssuranceFinding[],
): readonly AgentFindingRank[] {
  const byAgent = new Map<string, { count: number; worst: Severity }>();
  for (const f of findings) {
    const agent = f.agent_sub;
    if (agent === null || agent === undefined || agent.length === 0) continue;
    const entry = byAgent.get(agent) ?? { count: 0, worst: "other" as Severity };
    entry.count += 1;
    if (severityRank(f.severity) > severityRank(entry.worst)) entry.worst = f.severity;
    byAgent.set(agent, entry);
  }

  const rows: AgentFindingRank[] = [];
  for (const [agent, entry] of byAgent) {
    rows.push({ agent, count: entry.count, worstSeverity: entry.worst });
  }
  return rows.sort(
    (a, b) =>
      b.count - a.count ||
      severityRank(b.worstSeverity) - severityRank(a.worstSeverity) ||
      a.agent.localeCompare(b.agent),
  );
}

/* ---- Summary tile derivations --------------------------------------------------------------- */

/**
 * The open critical + high count, summed from the summary's `open_findings_by_severity`. Returns the
 * summed value and a combined `Measure`: `unverifiable` when any contributing count was null (no
 * data), else `estimate` when any was sampled, else `exact` — so a tile the server could not verify
 * is never shown as a reassuring exact number (design 12.3). When every contributor is null the value
 * is `null`, so the tile renders its honest "no reading" empty state.
 */
export function openCriticalHigh(summary: AssuranceSummary): {
  readonly value: number | null;
  readonly measure: Measure;
} {
  const rows = summary.open_findings_by_severity.filter(
    (r) => r.severity === "critical" || r.severity === "high",
  );
  return sumQuantities(rows.map((r) => r.count));
}

/**
 * Sum a set of `Quantity`s, returning the summed present values and a combined measure. `null` is
 * treated as "nothing to add"; the value is `null` only when EVERY contributor was null (the whole
 * total is unverifiable). Combined measure: `unverifiable` if any null, else `estimate` if any
 * sampled, else `exact`.
 */
export function sumQuantities(quantities: readonly Quantity[]): {
  readonly value: number | null;
  readonly measure: Measure;
} {
  let total = 0;
  let sawValue = false;
  let anyNull = false;
  let anyEstimate = false;
  for (const q of quantities) {
    if (q.value === null) {
      anyNull = true;
      continue;
    }
    total += q.value;
    sawValue = true;
    if (q.exactness === "estimated") anyEstimate = true;
  }
  const measure: Measure = anyNull ? "unverifiable" : anyEstimate ? "estimate" : "exact";
  return { value: sawValue ? total : null, measure };
}

/* ---- Emptiness: the GATEWAY_ASSURANCE empty state ------------------------------------------- */

/**
 * Whether a single `Quantity` carries nothing: either no data (null) or an explicit zero. Used to
 * decide the GATEWAY_ASSURANCE empty state.
 */
function quantityIsBlank(q: Quantity): boolean {
  return q.value === null || q.value === 0;
}

/** Whether a plain `count` is zero (the findings-page breakdowns are plain counts, not Quantity). */
function countIsBlank(c: Count): boolean {
  return c === 0;
}

/**
 * Whether the assurance data is entirely empty — every summary breakdown blank (zero or no-data), the
 * block rate and MTTF blank, AND no findings on the page. This is EXACTLY what an operator sees when
 * the Gateway's `GATEWAY_ASSURANCE` flag is off (its default): no scans, no findings, no decisions
 * ever reach the pipeline, so every zero-filled breakdown is zero and the findings list is empty.
 * The screen uses this to show an informative empty state explaining the flag rather than a wall of
 * zero tiles that would read as "all clear" when in fact assurance is simply not enabled (design
 * 12.3: empty states explain the cause; "unverifiable is never green").
 *
 * It is deliberately conservative: a single real scan or finding (any non-zero count anywhere) flips
 * it to the healthy READY view, so a genuinely quiet-but-enabled deployment still renders its real
 * zeros in the tiles and tables rather than the flag explainer.
 */
export function isAssuranceEmpty(
  summary: AssuranceSummary,
  findings: AssuranceFindingPage,
): boolean {
  const summaryBlank =
    summary.scans_by_verdict.every((r) => quantityIsBlank(r.count)) &&
    summary.scans_by_surface.every((r) => quantityIsBlank(r.count)) &&
    summary.open_findings_by_severity.every((r) => quantityIsBlank(r.count)) &&
    summary.open_findings_by_engine.every((r) => quantityIsBlank(r.count)) &&
    quantityIsBlank(summary.block_rate) &&
    quantityIsBlank(summary.mean_time_to_fix_s);
  const findingsBlank =
    findings.items.length === 0 &&
    findings.by_severity.every((r) => countIsBlank(r.count)) &&
    findings.by_engine.every((r) => countIsBlank(r.count));
  return summaryBlank && findingsBlank;
}

/* ---- Drill-down to /events ------------------------------------------------------------------ */

/**
 * Build a relative `/events` link from a set of filter params. Values are encoded by
 * `URLSearchParams`; empty values are dropped. The assurance endpoints attach no server-built
 * `drill`, so the screen composes honest standing filters from real `/events` params — never an
 * invented route — consistent with the enforcement/posture screens. The events screen reads them.
 */
function eventsHref(params: Readonly<Record<string, string>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value.length > 0) search.set(key, value);
  }
  const qs = search.toString();
  return qs.length > 0 ? `/events?${qs}` : "/events";
}

/** The `/events` filter for the scans of a given verdict. */
export function scansByVerdictHref(verdict: Verdict): string {
  return eventsHref({ category: "assurance", event: "assurance_scan", verdict });
}

/** The `/events` filter for the scans on a given surface. */
export function scansBySurfaceHref(surface: Surface): string {
  return eventsHref({ category: "assurance", event: "assurance_scan", surface });
}

/**
 * The `/events` filter for a single assurance finding. Carries the fingerprint, severity and status,
 * plus the engine and agent when present (both are nullable per contract), so a finding drills to
 * exactly the events behind it (design 12.3).
 */
export function findingHref(finding: AssuranceFinding): string {
  const base: Record<string, string> = {
    category: "assurance",
    event: "assurance_finding",
    fingerprint: finding.fingerprint,
    severity: finding.severity,
    status: finding.status,
  };
  if (finding.engine !== null && finding.engine !== undefined) base["engine"] = finding.engine;
  if (finding.agent_sub !== null && finding.agent_sub !== undefined) {
    base["agent"] = finding.agent_sub;
  }
  return eventsHref(base);
}

/** The `/events` filter for all findings introduced by a given agent. */
export function agentFindingsHref(agent: string): string {
  return eventsHref({ category: "assurance", event: "assurance_finding", agent });
}
